import { PageContainer, ProTable } from '@ant-design/pro-components';
import { Typography } from 'antd';
import { useRef, useState } from 'react';
import { useOrganizationPath } from '@/common/hooks/use-organization-path';
import {
  queryTraceAccountLogs,
  queryTraceCommentLogs,
  queryTraceContentLogs,
} from '../services/trace';

const ACCOUNT_TYPE = {
  own: {
    text: '自有账号',
    status: 'Success',
  },
  rival: {
    text: '竞品账号',
    status: 'Processing',
  },
};

const PLATFORM = {
  douyin: {
    text: '抖音',
    status: 'Processing',
  },
  weibo: {
    text: '微博',
    status: 'Default',
  },
  xhs: {
    text: '小红书',
    status: 'Success',
  },
};

const getDateText = (value) => {
  if (!value) {
    return undefined;
  }
  return value.format ? value.format('YYYY-MM-DD') : value;
};

const getRangeQuery = (value) => ({
  end: getDateText(value?.[1]),
  start: getDateText(value?.[0]),
});

const timeRangeColumn = {
  title: '时间范围',
  dataIndex: 'createdAtRange',
  hideInTable: true,
  search: {
    transform: getRangeQuery,
  },
  valueType: 'dateRange',
};

const traceAccountColumns = [
  {
    title: '账号类型',
    dataIndex: 'accountType',
    valueEnum: ACCOUNT_TYPE,
    fixed: 'left',
    width: 120,
  },
  { title: '账号名称', dataIndex: 'name', fixed: 'left', width: 160 },
  { title: '平台', dataIndex: 'platform', valueEnum: PLATFORM, fixed: 'left', width: 120 },
];

const accountColumns = [
  ...traceAccountColumns,
  timeRangeColumn,
  { title: '粉丝数', dataIndex: 'followerCount', valueType: 'digit', search: false },
  { title: '获赞数', dataIndex: 'likeCount', valueType: 'digit', search: false },
  { title: '收藏数', dataIndex: 'collectCount', valueType: 'digit', search: false },
  { title: '作品数', dataIndex: 'contentCount', valueType: 'digit', search: false },
  { title: '记录时间', dataIndex: 'createdAt', valueType: 'dateTime', search: false },
];

const commentColumns = [
  ...traceAccountColumns,
  timeRangeColumn,
  { title: '评论用户', dataIndex: 'userName' },
  { title: '评论内容', dataIndex: 'content', ellipsis: true },
  { title: '获赞数', dataIndex: 'likeCount', valueType: 'digit', search: false },
  { title: '评论时间', dataIndex: 'createdAt', valueType: 'dateTime', search: false },
];

const DataTrace = () => {
  const [activeTab, setActiveTab] = useState('account');
  const actionRef = useRef(null);
  const { organizationId } = useOrganizationPath();

  const contentColumns = [
    ...traceAccountColumns,
    timeRangeColumn,
    {
      title: '内容标题',
      dataIndex: 'title',
      ellipsis: true,
      renderText: (value) => (value?.length > 30 ? `${value.slice(0, 30)}...` : value),
    },
    { title: '获赞数', dataIndex: 'likeCount', valueType: 'digit', search: false },
    { title: '评论数', dataIndex: 'commentCount', valueType: 'digit', search: false },
    { title: '转发数', dataIndex: 'shareCount', valueType: 'digit', search: false },
    { title: '收藏数', dataIndex: 'collectCount', valueType: 'digit', search: false },
    {
      title: '视频地址',
      dataIndex: 'videoUrl',
      search: false,
      render: (_, record) =>
        record.videoUrl ? (
          <Typography.Link href={record.videoUrl} target="_blank" rel="noreferrer">
            视频地址
          </Typography.Link>
        ) : (
          '-'
        ),
    },
    { title: '记录时间', dataIndex: 'createdAt', valueType: 'dateTime', search: false },
  ];

  const tableConfig = {
    account: {
      columns: accountColumns,
      headerTitle: '账号日志',
      request: queryTraceAccountLogs,
    },
    content: {
      columns: contentColumns,
      headerTitle: '内容日志',
      request: queryTraceContentLogs,
    },
    comment: {
      columns: commentColumns,
      headerTitle: '评论日志',
      request: queryTraceCommentLogs,
    },
  }[activeTab];

  return (
    <PageContainer
      content="查看当前组织的账号、内容和评论监测日志。"
      tabActiveKey={activeTab}
      onTabChange={(key) => {
        setActiveTab(key);
        actionRef.current?.reload?.();
      }}
      tabList={[
        { key: 'account', tab: '账号日志' },
        { key: 'content', tab: '内容日志' },
        { key: 'comment', tab: '评论日志' },
      ]}
    >
      <ProTable
        actionRef={actionRef}
        rowKey="id"
        headerTitle={tableConfig.headerTitle}
        request={(params) => tableConfig.request({ ...params, organizationId })}
        columns={tableConfig.columns}
        search={{ defaultCollapsed: false, labelWidth: 96 }}
        scroll={{ x: 'max-content' }}
      />
    </PageContainer>
  );
};

export default DataTrace;
